import { apiRequest } from '@/lib/api';

const PKCE_STORAGE_PREFIX = 'oauth.pkce.';

function base64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis
    .btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '');
}

export async function createPkcePair(webCrypto = globalThis.crypto) {
  const verifierBytes = new Uint8Array(32);
  webCrypto.getRandomValues(verifierBytes);
  const verifier = base64Url(verifierBytes);
  const digest = await webCrypto.subtle.digest(
    'SHA-256',
    new globalThis.TextEncoder().encode(verifier),
  );

  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

export async function beginNativeOauth(provider) {
  const { verifier, challenge } = await createPkcePair();
  const transaction = await apiRequest(provider.path, 'POST', {
    provider: provider.key,
    flow: 'native',
    code_challenge: challenge,
  });

  sessionStorage.setItem(
    `${PKCE_STORAGE_PREFIX}${transaction.state}`,
    verifier,
  );

  const { Browser } = await import('@capacitor/browser');
  try {
    await Browser.open({ url: transaction.authorizationUrl });
  } catch (error) {
    sessionStorage.removeItem(`${PKCE_STORAGE_PREFIX}${transaction.state}`);
    throw error;
  }
}

export async function exchangeNativeOauth(path) {
  const callbackUrl = new URL(path, window.location.origin);
  const state = callbackUrl.searchParams.get('state');
  const code = callbackUrl.searchParams.get('code');
  const storageKey = `${PKCE_STORAGE_PREFIX}${state}`;
  const codeVerifier = sessionStorage.getItem(storageKey);

  if (!state || !code || !codeVerifier)
    throw new Error('Invalid OAuth callback');

  try {
    // The account was created in the system browser, which can't know the app's
    // language, so send it here for a new account to adopt
    // (Users::AdoptSignupLocale).
    return await apiRequest(callbackUrl.pathname, 'POST', {
      state,
      code,
      code_verifier: codeVerifier,
      locale: document.documentElement.lang,
    });
  } finally {
    sessionStorage.removeItem(storageKey);
  }
}
